import React from 'react';
import { PopUp } from '@/components/design-system/molecules/pop-up';
import { Modal } from '@/components/design-system/atoms/modal';
import { getDiscountCategoryLabel } from '../contract-discount-row/utils';
import { useTranslation } from 'react-i18next';

export type DiscountInfoModalProps = {
  open: boolean;
  onClose: () => void;
  discount: any;
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const DiscountInfoModal: React.FC<DiscountInfoModalProps> = ({ open, onClose, discount }) => {
  const { t } = useTranslation();
  if (!open || !discount) return null;
  console.log(discount);
  return (
    <Modal open={open} onClose={onClose}>
      <PopUp
        type="default"
        title={t('js.features.admin.contracts.discount_info_modal.title') as string}
        primaryButtonProps={{
          children: t('js.features.admin.contracts.discount_info_modal.edit_button') as string,
          onClick: () => {},
          className: 'flex-1 hidden',
          disabled: true, // Not implemented yet
        }}
        secondaryButtonProps={{
          children: t('js.features.admin.contracts.discount_info_modal.cancel_button') as string,
          onClick: onClose,
          className: 'flex-1',
          variant: 'ghost',
        }}
        buttonOrder="primary-first"
      >
        <PopUp.PopUpInput>
          <div className="flex flex-col">
            <div className="mb-4 grid grid-cols-2 gap-x-8 gap-y-4">
              <div>
                <div className="mb-1 text-[14px] text-N700">
                  {t('js.features.admin.contracts.discount_info_modal.fields.amount')}
                </div>
                <div className="text-[16px] font-bold text-N900">
                  €{(discount.amount_cents / 100).toFixed(2)}
                </div>
              </div>
              <div>
                <div className="mb-1 text-[14px] text-N700">
                  {t('js.features.admin.contracts.discount_info_modal.fields.unit')}
                </div>
                <div className="text-[16px] font-bold text-N900">
                  {discount.measurement === 'percent'
                    ? t('js.features.admin.contracts.discount_info_modal.units.percent')
                    : t('js.features.admin.contracts.discount_info_modal.units.fixed')}
                </div>
              </div>
              <div>
                <div className="mb-1 text-[14px] text-N700">
                  {t('js.features.admin.contracts.discount_info_modal.fields.category')}
                </div>
                <div className="text-[16px] font-bold text-N900">
                  {getDiscountCategoryLabel(discount.discount_category) || '-'}
                </div>
              </div>
              <div>
                <div className="mb-1 text-[14px] text-N700">
                  {t('js.features.admin.contracts.discount_info_modal.fields.period')}
                </div>
                <div className="text-[16px] font-bold text-N900">
                  {discount.period
                    ? discount.period.charAt(0).toUpperCase() +
                      discount.period.slice(1).replace('_', ' ')
                    : '-'}
                </div>
              </div>
              <div>
                <div className="mb-1 text-[14px] text-N700">
                  {t('js.features.admin.contracts.discount_info_modal.fields.start_date')}
                </div>
                <div className="text-[16px] font-bold text-N900">
                  {formatDate(discount.start_date) || '-'}
                </div>
              </div>
              <div>
                <div className="mb-1 text-[14px] text-N700">
                  {t('js.features.admin.contracts.discount_info_modal.fields.end_date')}
                </div>
                <div className="text-[16px] font-bold text-N900">
                  {formatDate(discount.end_date) || '-'}
                </div>
              </div>
            </div>
            <div className="mb-4">
              <div className="mb-1 text-[14px] text-N700">
                {t('js.features.admin.contracts.discount_info_modal.fields.note')}
              </div>
              <div
                className="whitespace-pre-line text-[16px] font-bold text-N900"
                dangerouslySetInnerHTML={{ __html: discount.note || '-' }}
              />
            </div>
            <div>
              <div className="mb-1 text-[14px] text-N700">
                {t('js.features.admin.contracts.discount_info_modal.fields.attachment')}
              </div>
              <div className="flex flex-wrap gap-4">
                {discount.attachments && discount.attachments.length > 0 ? (
                  discount.attachments.map((att: any, idx: number) => {
                    const url = typeof att === 'object' ? att.url : att;
                    const filename =
                      typeof att === 'object'
                        ? att.filename
                        : url.substring(url.lastIndexOf('/') + 1);
                    const isImage = /\.(jpg|jpeg|png)$/i.test(url);

                    return (
                      <a
                        key={idx}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ width: '72px', height: '72px', borderRadius: '12px' }}
                        className="relative block flex items-center justify-center overflow-hidden border border-gray-200 bg-white shadow-lg"
                      >
                        {isImage ? (
                          <img
                            src={url}
                            alt={filename}
                            className="h-[100px] w-[100px] object-contain"
                          />
                        ) : (
                          <span className="break-all p-2 text-center text-xs text-gray-400">
                            {filename}
                          </span>
                        )}
                      </a>
                    );
                  })
                ) : (
                  <span className="text-xs text-gray-400">
                    {t('js.features.admin.contracts.discount_info_modal.no_attachments')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </PopUp.PopUpInput>
      </PopUp>
    </Modal>
  );
};

export default DiscountInfoModal;
